import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, of } from 'rxjs';

import { Error } from '../business/error';
import { ErrorPageService } from '../services/error-page.service';
import { ToggleService } from '../services/toggle.service';

@Injectable({
    providedIn: 'root',
})
export class ToggleGuard {
    public constructor(
        private errorPageService: ErrorPageService,
        private toggleService: ToggleService,
        private translateService: TranslateService,
    ) {}

    /**
     * Activates a route based on the given "data.toggle"
     * Redirects to error view if :
     * - the toggle found is set to false (using toggle service)
     * - there is no toggle found (using toggle service)
     * @param {ActivatedRouteSnapshot} route
     * @param {RouterStateSnapshot} state
     * @returns {Observable<boolean>} Could the route be activated
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        if (route.data !== undefined && route.data.toggle !== undefined) {
            const toggleKey: string = route.data.toggle;
            return this.toggleService.getFeatureStatus(toggleKey).pipe(
                map((status: boolean) => {
                    if (!status) {
                        this.handleToggledOff(toggleKey);
                    }
                    return status;
                }),
            );
        } else {
            return of(false);
        }
    }

    /**
     * Checks that children route are authorized
     * @param {ActivatedRouteSnapshot} childRoute
     * @param {RouterStateSnapshot} state
     * @returns {Observable<boolean>}
     */
    public canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.canActivate(childRoute, state);
    }

    /**
     * Gets translation for the given key (use default translation if the given key is not valid)
     * @param {string} toggleKey (ex: FEATURE1.SERVICE2.SOUS-SERVICE3)
     * @param {any} translate
     * @returns {any | null} Translation for the given key or null if the key is not valid
     */
    private getServiceTranslate(toggleKey: string, translate: any): any | null {
        const toggleParts: string[] = toggleKey.split('.');
        toggleParts.shift();
        let validToggleKey = true;
        if (toggleParts && toggleParts.length > 0) {
            toggleParts.forEach((togglePart: string) => {
                if (Object.prototype.hasOwnProperty.call(translate, togglePart.toLowerCase())) {
                    translate = translate[togglePart.toLowerCase()];
                } else {
                    validToggleKey = false;
                }
            });
        } else {
            validToggleKey = false;
        }
        if (
            !Object.prototype.hasOwnProperty.call(translate, 'title') ||
            !Object.prototype.hasOwnProperty.call(translate, 'description')
        ) {
            validToggleKey = false;
        }
        return validToggleKey ? translate : null;
    }

    /**
     * When the toggle is explicitly set to FALSE or is missing from env file
     * @param {string} toggleKey (from route.data.toggle)
     */
    private handleToggledOff(toggleKey: string): void {
        this.translateService.use('i18n');
        const translateKey: string = toggleKey.split('.')[0].concat('.toggles');
        this.translateService.get(translateKey.toLowerCase()).subscribe((translate: any) => {
            const toggleTranslate = this.getServiceTranslate(toggleKey, translate);
            if (toggleTranslate) {
                const translateTitle: string = toggleTranslate['title'];
                const translateDescription: string = toggleTranslate['description'];
                const error: Error = new Error({
                    title: translateTitle,
                    description: translateDescription,
                });
                this.errorPageService.showCustomErrorPage(error);
            } else {
                this.errorPageService.showDefaultErrorPage();
            }
        });
    }
}
